import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Star, Bike, Clock, Smile, Loader2, CheckCircle2, MessageSquare
} from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

function StarRating({ value, onChange, label, icon: Icon }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="space-y-2" data-testid={`rating-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Icon className="w-4 h-4 text-emerald-600" />
        {label}
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(s => (
          <button
            key={s}
            type="button"
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(s)}
            className="p-0.5 transition-transform hover:scale-110"
            data-testid={`star-${label.toLowerCase()}-${s}`}
          >
            <Star
              className={`w-9 h-9 transition-colors ${
                s <= (hover || value) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function RateEstafetaPage() {
  const { restaurantId, orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [punctuality, setPunctuality] = useState(0);
  const [friendliness, setFriendliness] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/v1/deliverers/rate/${restaurantId}/${orderId}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.already_rated) setSubmitted(true);
      })
      .catch(() => setError("Pedido no encontrado"))
      .finally(() => setLoading(false));
  }, [restaurantId, orderId]);

  const handleSubmit = async () => {
    if (punctuality === 0 || friendliness === 0) {
      toast.error("Por favor, puntua ambas categorias");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/v1/deliverers/rate/${restaurantId}/${orderId}`, {
        punctuality, friendliness, comment
      });
      setSubmitted(true);
      toast.success("Gracias por tu evaluacion!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al enviar";
      toast.error(msg);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="text-center">
          <Bike className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-700 mb-2">Pedido no encontrado</h1>
          <p className="text-gray-500">El link puede haber expirado</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const rating = info?.rating;
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-600 to-emerald-800 flex items-center justify-center px-6" data-testid="rating-submitted">
        <div className="text-center text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-5 backdrop-blur-sm">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Gracias!</h1>
          <p className="text-emerald-200 mb-6">Tu evaluacion ha sido registrada</p>
          {rating && (
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm space-y-2">
              <div className="flex justify-center gap-0.5">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-5 h-5 ${s <= rating.overall ? "text-yellow-400 fill-yellow-400" : "text-white/30"}`} />
                ))}
              </div>
              <p className="text-sm text-emerald-100">{rating.overall}/5 para {info.deliverer_name}</p>
            </div>
          )}
          <p className="text-xs text-emerald-300 mt-8">Powered by Kaiso System</p>
        </div>
      </div>
    );
  }

  const vehicleLabel = { moto: "Moto", bicycle: "Bicicleta", bici: "Bicicleta", coche: "Auto" };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="rate-estafeta-page">
      {/* Header */}
      <div className="bg-emerald-600 text-white px-6 py-8 text-center">
        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
          <Bike className="w-8 h-8" />
        </div>
        <h1 className="text-xl font-bold mb-1">Evalua a tu estafeta</h1>
        <p className="text-emerald-200 text-sm">Pedido #{orderId.slice(-6).toUpperCase()}</p>
      </div>

      <div className="max-w-md mx-auto px-6 -mt-4 space-y-5 pb-8">
        {/* Driver Info Card */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="text-lg font-bold text-emerald-700">{info?.deliverer_name?.charAt(0) || "?"}</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900" data-testid="deliverer-name">{info?.deliverer_name}</h3>
              <p className="text-sm text-gray-500">{vehicleLabel[info?.deliverer_vehicle] || info?.deliverer_vehicle}</p>
            </div>
            {info?.deliverer_total_ratings > 0 && (
              <div className="ml-auto text-right">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="font-bold text-sm">{info.deliverer_avg_rating}</span>
                </div>
                <p className="text-[10px] text-gray-400">{info.deliverer_total_ratings} evaluaciones</p>
              </div>
            )}
          </div>
        </div>

        {/* Rating Form */}
        <div className="bg-white rounded-xl shadow-sm border p-5 space-y-5">
          <StarRating value={punctuality} onChange={setPunctuality} label="Puntualidad" icon={Clock} />
          <StarRating value={friendliness} onChange={setFriendliness} label="Simpatia" icon={Smile} />

          {/* Comment */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MessageSquare className="w-4 h-4 text-emerald-600" />
              Comentario (opcional)
            </div>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Algo que quieras compartir..."
              className="w-full rounded-lg border border-gray-200 p-3 text-sm resize-none h-20 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="rating-comment"
            />
          </div>

          {/* Average Preview */}
          {punctuality > 0 && friendliness > 0 && (
            <div className="bg-emerald-50 rounded-lg p-3 text-center" data-testid="rating-preview">
              <div className="flex justify-center gap-0.5 mb-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-5 h-5 ${s <= Math.round((punctuality + friendliness) / 2) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} />
                ))}
              </div>
              <p className="text-xs text-emerald-700 font-medium">{((punctuality + friendliness) / 2).toFixed(1)} / 5</p>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={submitting || punctuality === 0 || friendliness === 0}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold text-base"
            data-testid="submit-rating-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "ENVIAR EVALUACION"}
          </Button>
        </div>

        <p className="text-xs text-gray-400 text-center">Powered by Kaiso System</p>
      </div>
    </div>
  );
}
